<script setup>
import FixtureCell from '~/components/fixtures/FixtureCell.vue';
import TeamCard from '~/components/teams/TeamCard.vue';
import BracketControls from '~/components/tournaments/BracketControls.vue';
import BracketMinimap from '~/components/tournaments/BracketMinimap.vue';
import { computed, ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import { useBracketPanZoom } from '~/composables/useBracketPanZoom';
import FixtureResultRow from "~/components/fixtures/FixtureResultRow.vue";

const route = useRoute();

const props = defineProps({
  stages: {
    type: Array,
    required: true,
    default: () => []
  },
  allowEditing: {
    type: Boolean,
    default: true
  }
});

// Constants for layout calculations
const FIXTURE_CARD_HEIGHT = 130; // Actual rendered height of fixture card
const FIXTURE_GAP = 12; // Gap between fixtures in first round
const FIXTURE_HEIGHT = FIXTURE_CARD_HEIGHT + FIXTURE_GAP; // Total spacing unit: 188 + 12 = 200px
const ROUND_WIDTH = 350; // Horizontal spacing between rounds
const CONNECTOR_WIDTH = 50; // Width of connector lines extending from fixtures

// Get total rounds from stages
const totalRounds = computed(() => {
  if (!props.stages || props.stages.length === 0) return 0;
  return Math.max(...props.stages.map(s => s.round || 1));
});

// Group stages by round for display
const roundGroups = computed(() => {
  const groups = {};
  const positionsByRound = {}; // Track Y positions for each round/bracketPosition
  const bracketPositionsByRound = {}; // Track sorted array of bracket positions for each round

  // Use props.stages
  if (!props.stages || props.stages.length === 0) {
    return [];
  }

  // First pass: calculate positions for first round (tightest spacing)
  const firstRound = Math.max(...props.stages.map(s => s.round || 1));

  // Sort stages by round (highest first) to ensure parents are processed before children
  const sortedStages = [...props.stages].sort((a, b) => (b.round || 1) - (a.round || 1));

  sortedStages.forEach(stage => {
    const round = stage.round || 1;
    const isThirdPlace = stage.type === 3;

    // Third place playoff gets special treatment - same X as final, but positioned separately
    const groupKey = isThirdPlace ? 'thirdPlace' : round;

    if (!groups[groupKey]) {
      groups[groupKey] = {
        round,
        name: stage.name,
        x: (totalRounds.value - round) * ROUND_WIDTH,
        items: [],
        isThirdPlace
      };
    }

    const fixtures = (stage.fixtures || []).sort((a, b) =>
      (a.tournamentStageSort ?? 0) - (b.tournamentStageSort ?? 0)
    );
    const teams = stage.teams || [];
    const byeTeams = teams.filter(t => t.isBye);

    // Collect all items (fixtures and byes) for this round
    const allItems = [];

    // Collect fixtures with their bracket positions
    fixtures.forEach((fixture, index) => {
      // Use tournamentStageSort from backend for consistent positioning
      const bracketPosition = (fixture.tournamentStageSort ?? index) + 1;
      allItems.push({
        type: 'fixture',
        fixture,
        bracketPosition
      });
    });

    // Collect BYE teams. team.bracketPosition is a POSITION (1..bracketSize, two per
    // match slot) while fixture.tournamentStageSort is a SLOT (0..bracketSize/2 - 1), so
    // convert the bye's position to its slot to share the fixtures' keyspace — a bye at
    // position p occupies slot floor((p - 1) / 2).
    byeTeams.forEach(team => {
      allItems.push({
        type: 'bye',
        team,
        bracketPosition: Math.floor(((team.bracketPosition ?? 1) - 1) / 2) + 1
      });
    });

    // Sort all items by bracketPosition
    allItems.sort((a, b) => a.bracketPosition - b.bracketPosition);

    // Store sorted bracket positions for this round (for parent lookups)
    if (!isThirdPlace) {
      bracketPositionsByRound[round] = allItems.map(item => item.bracketPosition);
    }

    // For first round, use tight spacing based on INDEX, not bracketPosition value
    // For later rounds, calculate based on parent positions
    const isFirstRound = round === firstRound;

    // Process sorted items
    allItems.forEach((item, index) => {
      let yPos;
      const bracketPosition = item.bracketPosition;

      if (isThirdPlace) {
        // Third place playoff: position below the final with some spacing
        // Find the final's position (round 1, last item)
        const finalRoundPositions = positionsByRound[1];
        if (finalRoundPositions) {
          const finalPositions = Object.values(finalRoundPositions);
          const maxFinalY = Math.max(...finalPositions);
          // Position third place 1.5x fixture height below the final
          yPos = maxFinalY + FIXTURE_HEIGHT * 1.5;
        } else {
          // Fallback if final doesn't exist yet
          yPos = FIXTURE_HEIGHT * 2;
        }
      } else if (isFirstRound) {
        // First round: tight stacking with 12px gaps using INDEX
        yPos = index * FIXTURE_HEIGHT;
      } else {
        // Later rounds: position at midpoint between parent fixtures
        // Use sorted index to find parent fixtures (works with sparse bracket positions)
        const parentRound = round + 1;
        const mySortedIndex = index; // Current item's position in sorted array
        const parent1SortedIndex = mySortedIndex * 2;
        const parent2SortedIndex = mySortedIndex * 2 + 1;

        // Look up parent bracket positions from sorted array
        const parentBracketPositions = bracketPositionsByRound[parentRound] || [];
        const parent1BracketPos = parentBracketPositions[parent1SortedIndex];
        const parent2BracketPos = parentBracketPositions[parent2SortedIndex];

        // Get Y positions for parent fixtures
        const parent1Pos = positionsByRound[parentRound]?.[parent1BracketPos];
        const parent2Pos = positionsByRound[parentRound]?.[parent2BracketPos];

        if (parent1Pos !== undefined && parent2Pos !== undefined) {
          // Both parents exist - center between them
          yPos = (parent1Pos + parent2Pos) / 2;
        } else if (parent1Pos !== undefined) {
          // Only first parent exists - estimate second parent position
          // Second parent would be FIXTURE_HEIGHT below first parent
          const estimatedParent2Pos = parent1Pos + FIXTURE_HEIGHT;
          yPos = (parent1Pos + estimatedParent2Pos) / 2;
        } else if (parent2Pos !== undefined) {
          // Only second parent exists - estimate first parent position
          const estimatedParent1Pos = parent2Pos - FIXTURE_HEIGHT;
          yPos = (estimatedParent1Pos + parent2Pos) / 2;
        } else {
          // Fallback
          yPos = index * FIXTURE_HEIGHT;
        }
      }

      // Store position using BRACKET POSITION for child calculations (not for third place)
      if (!isThirdPlace) {
        if (!positionsByRound[round]) positionsByRound[round] = {};
        positionsByRound[round][bracketPosition] = yPos;
      }

      groups[groupKey].items.push({
        type: item.type,
        fixture: item.fixture,
        team: item.team,
        stage,
        round,
        bracketPosition,
        y: yPos
      });
    });
  });

  // Sort items within each group by bracketPosition
  Object.values(groups).forEach(group => {
    group.items.sort((a, b) => a.bracketPosition - b.bracketPosition);
  });

  // Return groups sorted by round (highest first = earliest rounds on left)
  // Third place playoff goes at the end
  return Object.values(groups).sort((a, b) => {
    if (a.isThirdPlace) return 1;
    if (b.isThirdPlace) return -1;
    return b.round - a.round;
  });
});

// Flatten for rendering
const positionedItems = computed(() => {
  const items = [];
  roundGroups.value.forEach(group => {
    group.items.forEach(item => {
      items.push({
        ...item,
        x: group.x
      });
    });
  });
  return items;
});

// Calculate connector lines between rounds
const connectorLines = computed(() => {
  const lines = [];

  // Group items by round (excluding third place playoff)
  const itemsByRound = {};
  positionedItems.value.forEach(item => {
    // Skip third place playoff items - they don't get connectors
    if (item.stage?.type === 3) return;

    if (!itemsByRound[item.round]) {
      itemsByRound[item.round] = [];
    }
    itemsByRound[item.round].push(item);
  });

  // Sort items within each round by bracketPosition
  Object.keys(itemsByRound).forEach(key => {
    itemsByRound[key].sort((a, b) => a.bracketPosition - b.bracketPosition);
  });

  // For each round, create connectors to the next round
  Object.keys(itemsByRound).forEach(roundKey => {
    const round = parseInt(roundKey);
    const nextRound = round - 1;

    if (nextRound < 1) return; // No connectors from final round

    const currentItems = itemsByRound[round];
    const nextItems = itemsByRound[nextRound] || [];

    // Group items by sorted index pairs (0-1, 2-3, 4-5, etc.)
    // Process pairs based on position in sorted array, not actual bracketPosition value
    const processedIndexes = new Set();

    for (let i = 0; i < currentItems.length; i++) {
      // Skip if we already processed this item as part of a pair
      if (processedIndexes.has(i)) {
        continue;
      }

      const item1 = currentItems[i];
      const sortedIndex1 = i;

      // Find the pair: even indexes pair with odd (0-1, 2-3, 4-5, etc.)
      const sortedIndex2 = (sortedIndex1 % 2 === 0) ? sortedIndex1 + 1 : sortedIndex1 - 1;
      const item2 = currentItems[sortedIndex2]; // May be undefined if odd number of items

      // Helper to get team IDs from an item
      const getTeamIds = (item) => {
        if (item.type === 'fixture') {
          return [item.fixture.homeTeamId, item.fixture.awayTeamId].filter(id => id != null);
        } else {
          return [item.team.id];
        }
      };

      const item1TeamIds = getTeamIds(item1);
      const item2TeamIds = item2 ? getTeamIds(item2) : [];
      const allTeamIds = [...item1TeamIds, ...item2TeamIds];

      let targetItem = null;

      // Primary strategy: Find target by team ID matching (when teams are assigned)
      if (allTeamIds.length > 0) {
        targetItem = nextItems.find(nextItem => {
          const nextTeamIds = getTeamIds(nextItem);
          return allTeamIds.some(teamId => nextTeamIds.includes(teamId));
        });
      }

      // Fallback strategy: Use sorted index math (for pure TBC fixtures)
      if (!targetItem) {
        // Sorted indexes 0&1 connect to index 0, 2&3 to index 1, etc.
        const targetSortedIndex = Math.floor(sortedIndex1 / 2);
        targetItem = nextItems[targetSortedIndex];
      }

      if (!targetItem) continue;

      // Mark both indexes as processed
      processedIndexes.add(sortedIndex1);
      if (sortedIndex2 < currentItems.length) processedIndexes.add(sortedIndex2);

      // Calculate line positions (center of fixture is at y + half of card height)
      const line1X = item1.x + 300; // Fixture width is ~300px
      const line1Y = item1.y + (FIXTURE_CARD_HEIGHT / 2); // Center of fixture (94px)
      const halfwayX = line1X + (ROUND_WIDTH - 300) / 2; // Halfway between rounds

      // If there's a second item (pair), create Y-shaped connector
      if (item2) {
        const line2X = item2.x + 300;
        const line2Y = item2.y + (FIXTURE_CARD_HEIGHT / 2);

        // Horizontal lines from both items to the halfway point
        const h1Width = halfwayX - line1X;
        const h2Width = halfwayX - line2X;

        // Vertical line connecting the two horizontal lines
        const verticalY = Math.min(line1Y, line2Y);
        const verticalHeight = Math.abs(line2Y - line1Y);

        // Horizontal line from vertical midpoint to target fixture
        const midpointY = (line1Y + line2Y) / 2;
        const h3Width = targetItem.x - halfwayX;

        lines.push({
          id: `connector-${round}-${i}`,
          // Horizontal from item1 to halfway
          h1: { x: line1X, y: line1Y, width: h1Width },
          // Horizontal from item2 to halfway
          h2: { x: line2X, y: line2Y, width: h2Width },
          // Vertical connecting both at halfway point
          v: { x: halfwayX, y: verticalY, height: verticalHeight },
          // Horizontal from midpoint to target
          h3: { x: halfwayX, y: midpointY, width: h3Width }
        });
      } else {
        // Single item (BYE or odd number) - direct connection with angle if needed
        const targetY = targetItem.y + (FIXTURE_CARD_HEIGHT / 2);
        const h1Width = halfwayX - line1X;

        if (line1Y !== targetY) {
          // Need vertical adjustment
          const verticalHeight = Math.abs(targetY - line1Y);
          const h2Width = targetItem.x - halfwayX;

          lines.push({
            id: `connector-${round}-${i}`,
            h1: { x: line1X, y: line1Y, width: h1Width },
            h2: null,
            v: { x: halfwayX, y: Math.min(line1Y, targetY), height: verticalHeight },
            h3: { x: halfwayX, y: targetY, width: h2Width }
          });
        } else {
          // Straight line
          const totalWidth = targetItem.x - line1X;

          lines.push({
            id: `connector-${round}-${i}`,
            h1: { x: line1X, y: line1Y, width: totalWidth },
            h2: null,
            v: null,
            h3: null
          });
        }
      }
    }
  });

  return lines;
});

// Calculate total container dimensions
const containerDimensions = computed(() => {
  if (positionedItems.value.length === 0) {
    return { width: 0, height: 0 };
  }

  const maxX = Math.max(...positionedItems.value.map(item => item.x));
  const maxY = Math.max(...positionedItems.value.map(item => item.y));

  return {
    width: maxX + ROUND_WIDTH + 100, // Extra padding on right
    height: maxY + FIXTURE_HEIGHT + 160 // Extra padding on bottom + top padding for labels
  };
});

// Fullscreen state (from parent via v-model)
const fullscreen = defineModel('fullscreen', { type: Boolean, default: false });

// iOS Safari on iPhone has no Element fullscreen API — emulate with a fixed overlay
const usingCssFallback = ref(false);

const enterCssFallback = () => {
  usingCssFallback.value = true;
  document.documentElement.style.overflow = 'hidden';
};

const exitCssFallback = () => {
  usingCssFallback.value = false;
  document.documentElement.style.overflow = '';
};

// Minimap state
const minimapCollapsed = ref(false);

// Pan/Zoom setup
const contentRef = ref(null);
const containerRef = ref(null);
const containerWidth = ref(0);
const containerHeight = ref(0);

const {
  initialize,
  dispose,
  zoomIn,
  zoomOut,
  reset,
  pan,
  moveTo,
  currentState
} = useBracketPanZoom(contentRef, {
  maxZoom: 3,
  minZoom: 0.3,
  smoothScroll: false,
  bounds: true,
  boundsPadding: 0.1
});

// Update container dimensions for minimap
const updateContainerDimensions = () => {
  if (containerRef.value) {
    containerWidth.value = containerRef.value.clientWidth;
    containerHeight.value = containerRef.value.clientHeight;
  }
};

// Toggle fullscreen
const toggleFullscreen = () => {
  fullscreen.value = !fullscreen.value;
};

// Listen for fullscreen changes (user can exit with ESC)
const handleFullscreenChange = () => {
  if (!document.fullscreenElement) {
    fullscreen.value = false;
  }
};

// Toggle minimap
const toggleMinimap = () => {
  minimapCollapsed.value = !minimapCollapsed.value;
};

// Handle keyboard shortcuts
const handleKeyDown = (event) => {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const cmdOrCtrl = isMac ? event.metaKey : event.ctrlKey;

  // F for fullscreen toggle (always active)
  // if (event.key === 'f' || event.key === 'F') {
  //   event.preventDefault();
  //   toggleFullscreen();
  //   return;
  // }

  // Escape to exit fullscreen (always active)
  if (event.key === 'Escape' && fullscreen.value) {
    event.preventDefault();
    toggleFullscreen();
    return;
  }

  // Pan/Zoom shortcuts only work in fullscreen mode
  if (!fullscreen.value) return;

  // Arrow keys for panning
  if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
    event.preventDefault();
    const panStep = 50;

    switch (event.key) {
      case 'ArrowUp':
        pan(0, panStep);
        break;
      case 'ArrowDown':
        pan(0, -panStep);
        break;
      case 'ArrowLeft':
        pan(panStep, 0);
        break;
      case 'ArrowRight':
        pan(-panStep, 0);
        break;
    }
  }

  // +/= for zoom in
  if ((event.key === '+' || event.key === '=') && !cmdOrCtrl) {
    event.preventDefault();
    zoomIn();
  }

  // - for zoom out
  if (event.key === '-' && !cmdOrCtrl) {
    event.preventDefault();
    zoomOut();
  }
};

// Handle mouse wheel zoom (Cmd/Ctrl + scroll)
const handleWheel = (event) => {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const cmdOrCtrl = isMac ? event.metaKey : event.ctrlKey;

  if (cmdOrCtrl) {
    event.preventDefault();
    if (event.deltaY < 0) {
      zoomIn(0.1);
    } else {
      zoomOut(0.1);
    }
  }
};

// Minimap items for rendering
const minimapItems = computed(() => {
  return positionedItems.value.map(item => ({
    x: item.x,
    y: item.y + 60, // Account for top offset
    width: 300,
    height: FIXTURE_CARD_HEIGHT
  }));
});

// Handle minimap navigation
const handleMinimapNavigate = (x, y) => {
  moveTo(x, y);
};

// Watch fullscreen to enable/disable pan/zoom and trigger browser fullscreen
watch(fullscreen, async (isFullscreen, wasFullscreen) => {
  if (isFullscreen && !wasFullscreen) {
    // Entering fullscreen - native API where available, CSS overlay otherwise
    // (iPhone Safari has no Element.requestFullscreen)
    if (containerRef.value?.requestFullscreen && document.fullscreenEnabled) {
      try {
        if (!document.fullscreenElement) {
          await containerRef.value.requestFullscreen();
        }
      } catch (err) {
        console.error('Error entering fullscreen, using CSS fallback:', err);
        enterCssFallback();
      }
    } else {
      enterCssFallback();
    }

    nextTick(() => {
      initialize();
      updateContainerDimensions();

      // Add keyboard event listeners for pan/zoom
      if (containerRef.value) {
        containerRef.value.addEventListener('wheel', handleWheel, { passive: false });
      }
    });
  } else if (!isFullscreen && wasFullscreen) {
    // Exiting fullscreen
    // Reset pan/zoom before disposing
    reset();

    // Exit browser fullscreen if still in it
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error('Error exiting fullscreen:', err);
    }
    if (usingCssFallback.value) {
      exitCssFallback();
    }

    // Dispose and cleanup
    dispose();
    if (containerRef.value) {
      containerRef.value.removeEventListener('wheel', handleWheel);
    }
  }
});

// Watch for route changes - exit fullscreen on navigation
watch(() => [route.path, route.query], () => {
  if (fullscreen.value) {
    fullscreen.value = false;
  }
});

// Lifecycle
onMounted(() => {
  // Add keyboard event listeners (always active for fullscreen toggle)
  document.addEventListener('keydown', handleKeyDown);

  // Listen for fullscreen changes
  document.addEventListener('fullscreenchange', handleFullscreenChange);

  // Update container dimensions on window resize
  window.addEventListener('resize', updateContainerDimensions);

  updateContainerDimensions();
});

onBeforeUnmount(() => {
  dispose();
  if (usingCssFallback.value) {
    exitCssFallback();
  }
  document.removeEventListener('keydown', handleKeyDown);
  document.removeEventListener('fullscreenchange', handleFullscreenChange);
  if (containerRef.value) {
    containerRef.value.removeEventListener('wheel', handleWheel);
  }
  window.removeEventListener('resize', updateContainerDimensions);
});
</script>

<template>
  <div>
    <div
      ref="containerRef"
      class="bracket-hierarchical-container"
      :class="{ 'bracket-fullscreen-fallback': fullscreen && usingCssFallback }"
    >
      <!-- Fullscreen close alert -->
      <div
        v-if="fullscreen"
        class="fullscreen-alert pa-3 d-flex align-center"
        style="background: rgb(var(--v-theme-light)); border:1px solid rgb(var(--v-theme-borderLight)); border-radius:10px;"
      >
        <span class="flex-fill">{{ usingCssFallback ? 'Tap ✕ to exit fullscreen mode' : 'Press ESC or F to exit fullscreen mode' }}</span>
        <v-btn icon="mdi-close" variant="text" size="small" @click="toggleFullscreen"/>
      </div>

      <!-- Bracket Controls (only in fullscreen) -->
      <bracket-controls
        v-if="fullscreen"
        :fullscreen="fullscreen"
        :minimap-collapsed="minimapCollapsed"
        @toggle-fullscreen="toggleFullscreen"
        @zoom-in="zoomIn"
        @zoom-out="zoomOut"
        @reset-zoom="reset"
        @toggle-minimap="minimapCollapsed = !minimapCollapsed"
      />

      <!-- Bracket Minimap (only in fullscreen) -->
      <bracket-minimap
        v-if="fullscreen"
        :collapsed="minimapCollapsed"
        :bracket-width="containerDimensions.width"
        :bracket-height="containerDimensions.height"
        :items="minimapItems"
        :container-width="containerWidth"
        :container-height="containerHeight"
        :pan-zoom-state="currentState"
        @toggle-collapsed="minimapCollapsed = !minimapCollapsed"
        @navigate-to="handleMinimapNavigate"
      />

      <div
        ref="contentRef"
        class="bracket-hierarchical-content"
        :style="{
          width: `${containerDimensions.width}px`,
          height: `${containerDimensions.height}px`
        }"
      >
      <!-- Round Background Gradients -->


      <!-- Round Labels -->
      <div
        v-for="group in roundGroups"
        :key="`round-${group.round}-${group.isThirdPlace ? 'third' : 'main'}`"
        v-show="group.items.length > 0"
        class="round-label"
        :style="{
          left: `${group.x}px`,
          top: group.isThirdPlace && group.items.length > 0 ? `${group.items[0].y + 60 - 40}px` : '0px'
        }"
      >
        <h4 class="round-label-text mb-2">{{ group.name }}</h4>
      </div>

      <!-- Connector Lines -->
      <div
        v-for="line in connectorLines"
        :key="line.id"
        class="connector-group"
      >
        <!-- Horizontal line from first item -->
        <div
          v-if="line.h1"
          class="connector-line connector-horizontal"
          :style="{
            left: `${line.h1.x}px`,
            top: `${line.h1.y + 60}px`,
            width: `${line.h1.width}px`
          }"
        />

        <!-- Horizontal line from second item (if pair) -->
        <div
          v-if="line.h2"
          class="connector-line connector-horizontal"
          :style="{
            left: `${line.h2.x}px`,
            top: `${line.h2.y + 60}px`,
            width: `${line.h2.width}px`
          }"
        />

        <!-- Vertical line connecting pair -->
        <div
          v-if="line.v"
          class="connector-line connector-vertical"
          :style="{
            left: `${line.v.x}px`,
            top: `${line.v.y + 60}px`,
            height: `${line.v.height}px`
          }"
        />

        <!-- Horizontal line to target -->
        <div
          v-if="line.h3"
          class="connector-line connector-horizontal"
          :style="{
            left: `${line.h3.x}px`,
            top: `${line.h3.y + 60}px`,
            width: `${line.h3.width}px`
          }"
        />
      </div>

      <!-- Positioned Items (Fixtures and BYEs) -->
      <div
        v-for="item in positionedItems"
        :key="item.type === 'fixture' ? item.fixture.id : item.team.id"
        class="bracket-item"
        :style="{
          left: `${item.x}px`,
          top: `${item.y + 60}px`
        }"
      >
        <!-- Fixture Display -->
        <div v-if="item.type === 'fixture'" class="bracket-fixture">

          <fixture-result-row
              :style="'height: ' + FIXTURE_CARD_HEIGHT + 'px'"
            :fixture="item.fixture"
            class="mb-0"
            size="small"
            :allow-editing="allowEditing"
          />
        </div>

        <!-- BYE Display -->
        <div
          v-else
          class="bracket-bye"
          :style="'height: ' + FIXTURE_CARD_HEIGHT + 'px'"
        >
          <div class="d-flex align-center justify-center ga-2 pa-4">
            <team-card
              :team="item.team"
              size="small"
              :expandable="false"
            />
            <span class="tl-chip tl-chip--ok">BYE</span>
          </div>
        </div>
      </div>
    </div>
  </div>
  </div>
</template>

<style scoped lang="scss">
.fullscreen-alert {
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1002;
  max-width: 500px;
  cursor: pointer;
}

.bracket-hierarchical-container {
  width: 100%;
  overflow-x: auto;
  overflow-y: auto;
  position: relative;
  padding: 12px;
  &::-webkit-scrollbar {
    height: 8px;
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(var(--v-theme-on-surface), 0.05);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(var(--v-theme-on-surface), 0.2);
    border-radius: 4px;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.3);
    }
  }

  &:fullscreen {
    background: rgb(var(--v-theme-surface));
    padding: 24px;
  }

  /* CSS-emulated fullscreen for browsers without the Fullscreen API (iPhone Safari) */
  &.bracket-fullscreen-fallback {
    position: fixed;
    inset: 0;
    z-index: 2000;
    background: rgb(var(--v-theme-surface));
    padding: 16px;
  }
}

.bracket-hierarchical-content {
  position: relative;
  min-height: 400px;
}

.round-background {
  position: absolute;
  top: 0;
  z-index: 1;
  background: linear-gradient(to right, transparent 0%, transparent 70%, rgba(var(--v-theme-primary), 0.025) 100%);
  pointer-events: none;
}

.round-label {
  position: absolute;
  z-index: 15;
  width: 300px;
  padding: 8px 0;
}

.round-label-text {
  font-size: 10.5px;
  font-weight: 700;
  color: rgb(var(--v-theme-textSubtle));
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.bracket-item {
  position: absolute;
  z-index: 10;
  width: 300px;
}

.bracket-fixture {
  overflow: hidden;
}

.bracket-bye {
  border: 1.5px dashed rgb(var(--v-theme-success));
  border-radius: 12px;
  background: rgb(var(--v-theme-successTint));
  display: flex;
  align-items: center;
  justify-content: center;
}

.connector-line {
  position: absolute;
  z-index: 5;
  background: rgb(var(--v-theme-border));
}

.connector-horizontal {
  height: 2px;
}

.connector-vertical {
  width: 2px;
}

.connector-group {
  position: relative;
}
</style>
